import { useCallback, useState } from "react";
import {
	UseFormClearErrors,
	UseFormSetError,
	UseFormTrigger,
} from "react-hook-form";

import { AdminRoles, ValidationMessages } from "globalConstants";
import { isEmailAllowed, isFeatureEnabled } from "helpers";
import {
	useCheckIfEmailUniqueMutation,
	useGetWhitelistedDomainsMutation,
} from "queries";
import { validateEmailForUserType } from "validators";

type UseCheckIfEmailExistProps = {
	fieldName: string;
	fieldInitialValue?: string;
	trigger: UseFormTrigger<any>;
	setError: UseFormSetError<any>;
	clearErrors: UseFormClearErrors<any>;
	companyId?: string;
	errorMessage?: any;
};

/**
 * Custom hook for validating an email field in a react hook form.
 * It checks if the email is unique and if it's allowed based on a list of whitelisted domains.
 *
 * @returns handleChange: function to handle changes to the email field.
 * @returns handleBlur: function to handle when the email field loses focus.
 * @returns handleAgentOfficeChange: function to handle when the agent office changes.
 * @returns validator: function to validate the email field.
 * @returns {string[]} allowedDomains: array of allowed domains.
 * @returns {boolean} isLoading: if the hook is currently loading data.
 */
const useCheckIfEmailExist = ({
	fieldName,
	fieldInitialValue = "",
	trigger,
	setError,
	clearErrors,
	companyId,
	errorMessage = null,
}: UseCheckIfEmailExistProps) => {
	const [prevValue, setPrevValue] = useState<string>("");
	const [prevCompanyIdValue, setPrevCompanyIdValue] = useState<string>("");
	const [isEmailUsed, setIsEmailUsed] = useState<boolean>(false);
	const [isEmailNotAllowed, setIsEmailNotAllowed] = useState<boolean>(false);
	const [isEmailNotAllowedForUserType, setIsEmailNotAllowedForUserType] =
		useState<boolean>(false);

	const [allowedDomains, setAllowedDomains] = useState<string[]>([]);

	const validator = useCallback(() => {
		if (isEmailUsed) {
			return ValidationMessages.EmailExist;
		} else if (isEmailNotAllowedForUserType) {
			return ValidationMessages.NotAllowedEmailForUserType;
		} else if (isEmailNotAllowed) {
			return ValidationMessages.NotAllowedEmail;
		}
	}, [isEmailUsed, isEmailNotAllowedForUserType, isEmailNotAllowed]);

	const {
		mutateAsync: checkUniqueEmail,
		isLoading: isLoadingCheckUniqueEmail,
	} = useCheckIfEmailUniqueMutation();

	const {
		mutateAsync: getWhitelistedDomains,
		isLoading: isLoadingWhitelistedDomains,
	} = useGetWhitelistedDomainsMutation();

	const handleChange = useCallback(() => {
		setIsEmailUsed(false);
		setIsEmailNotAllowed(false);
	}, []);

	const checkUniqueEmailFn = useCallback(
		(value: string) =>
			checkUniqueEmail(value)
				.then(res => {
					if (res.user_email_exists === true) {
						setError(fieldName, {
							type: "emailValid",
							message: ValidationMessages.EmailExist,
						});

						setIsEmailUsed(true);
						return Promise.resolve(true);
					} else {
						return Promise.resolve(false);
					}
				})
				.catch(() => {}),
		[checkUniqueEmail, fieldName, setError],
	);

	const checkIfEmailAllowedFn = useCallback(
		(value: string, companyId: string | undefined) => {
			if (!value || !companyId) return;

			setPrevCompanyIdValue(companyId);

			return getWhitelistedDomains({ companyId })
				.then(domains => {
					if (!isEmailAllowed(value, domains)) {
						setError(fieldName, {
							type: "emailValid",
							message: ValidationMessages.NotAllowedEmail,
						});
						setIsEmailNotAllowed(true);
						setAllowedDomains(domains);
					} else {
						clearErrors(fieldName);
						setAllowedDomains([]);
					}
				})
				.catch(() => {});
		},
		[getWhitelistedDomains, setError, fieldName, clearErrors],
	);

	const validateEmailForUserTypeFn = useCallback(
		(email: string, role: string) => {
			validateEmailForUserType({
				email,
				role,
				preValidationCallback: () => {
					clearErrors(fieldName);
					setIsEmailNotAllowed(false);
					setIsEmailNotAllowedForUserType(false);
					setAllowedDomains([]);
				},
				errorCallback: () => {
					setError(fieldName, {
						type: "emailValid",
						message: ValidationMessages.NotAllowedEmailForUserType,
					});
					setIsEmailNotAllowedForUserType(true);
					setAllowedDomains([]);
				},
			});
		},
		[clearErrors, fieldName, setError, setAllowedDomains],
	);

	const handleBlur = useCallback(
		async (e: any, role: string) => {
			const value = e.target.value;
			if (!value || value === "" || value === prevValue) {
				return;
			}

			setPrevValue(value);
			const isValid = await trigger(fieldName);
			if (
				!isValid &&
				![
					ValidationMessages.NotAllowedEmailForUserType,
					ValidationMessages.NotAllowedEmail,
					ValidationMessages.EmailExist,
				].includes(errorMessage)
			) {
				return;
			}
			// check the email for uniqueness
			if (value !== fieldInitialValue) {
				const isEmailUsed = await checkUniqueEmailFn(value);
				if (isEmailUsed) {
					return;
				}
			}

			const isRiseAdminRole = Object.values(AdminRoles).includes(role);

			if (
				isRiseAdminRole &&
				isFeatureEnabled("ValidateEmailDomainForAdminUsers")
			) {
				validateEmailForUserTypeFn(value, role);
			} else {
				// then check for the domain's relevance
				checkIfEmailAllowedFn(value, companyId);
			}
		},
		[
			prevValue,
			trigger,
			fieldName,
			errorMessage,
			fieldInitialValue,
			checkUniqueEmailFn,
			checkIfEmailAllowedFn,
			companyId,
			validateEmailForUserTypeFn,
		],
	);
	// This function is triggered when the agent's office changes.
	// It checks if the email is allowed for the new agent office.
	const handleAgentOfficeChange = useCallback(
		(email, companyId, role) => {
			if (companyId === prevCompanyIdValue) {
				return;
			}
			setIsEmailNotAllowed(false);

			const isRiseAdminRole = Object.values(AdminRoles).includes(role);
			if (isRiseAdminRole) {
				return;
			}

			trigger(fieldName).then(isValid => {
				if (!isValid) return;

				if (companyId) {
					checkIfEmailAllowedFn(email, companyId);
				}
			});
		},
		[checkIfEmailAllowedFn, fieldName, prevCompanyIdValue, trigger],
	);

	const handleRoleChange = useCallback(
		async (value: string, role: string) => {
			if (!value) return;
			clearErrors(fieldName);
			setIsEmailNotAllowedForUserType(false);

			const isRiseAdminRole = Object.values(AdminRoles).includes(role);
			if (!isRiseAdminRole) return;

			const isValid = await trigger(fieldName);
			return isValid
				? validateEmailForUserTypeFn(value, role)
				: undefined;
		},
		[clearErrors, fieldName, trigger, validateEmailForUserTypeFn],
	);

	return {
		handleChange,
		handleBlur,
		handleAgentOfficeChange,
		handleRoleChange,
		validator,
		allowedDomains,
		isLoading: isLoadingCheckUniqueEmail || isLoadingWhitelistedDomains,
	};
};

export default useCheckIfEmailExist;
